"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import openclawLogo from "@/assets/openclaw-logo.png";

export const Hero = () => {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Subtle radial glow behind logo */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,hsl(var(--coral-bright)/0.12)_0%,transparent_70%)] pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center text-center px-6 py-24 md:py-32 max-w-4xl mx-auto">
        {/* Badge */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Badge
            variant="outline"
            className="mb-8 px-4 py-1.5 text-xs bg-[hsl(var(--coral-bright)/0.08)] text-primary font-mono uppercase tracking-widest border-0 backdrop-blur-sm"
          >
            OpenClaw as a Service
          </Badge>
        </motion.div>

        {/* Logo */}
        <motion.div
          className="mb-10"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <img src={openclawLogo} alt="Clawbiz mascot" className="w-48 h-auto md:w-64 mx-auto " />
        </motion.div>

        {/* Headline */}
        <motion.h1
          className="text-5xl md:text-6xl lg:text-7xl font-display font-medium leading-[1.05] tracking-tight mb-6"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          Clawbiz, <span className="gradient-text-cyan">handled</span> for you.
        </motion.h1>

        {/* Description */}
        <motion.p
          className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-8"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          We install, configure, and harden Clawbiz on your machine or cloud — wired to your chats, tools, and models.
          Your data. Your rules.
        </motion.p>

        {/* Feature bullets */}
        <motion.ul
          className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-foreground/80 mb-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          {[
            "Runs on your Mac, Windows, Linux, or VPS",
            "WhatsApp, Telegram, Discord, Slack, Signal",
            "Files, shell, browser, automation",
            "You own everything",
          ].map((item, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--cyan-bright))] shrink-0" />
              {item}
            </li>
          ))}
        </motion.ul>

        {/* CTAs */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Button size="lg" className="text-base px-8 h-12 glow-primary" asChild>
            <a href="#contact">Get Your Clawbiz Set Up</a>
          </Button>
          <Button
            size="lg"
            variant="ghost"
            className="text-base px-8 h-12 text-foreground/80 hover:text-foreground hover:bg-white/5"
            asChild
          >
            <a href="#services">See What We Set Up</a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
